// The parts of the drawer that change with the cart; CartDrawerSection.renderDrawer
// swaps these and leaves the rest alone.
const CART_DRAWER_PARTS = [
  "#CartDrawer-TotalQty",
  "#CartDrawer-Notices",
  "#CartDrawer-CartItems",
  "#CartDrawer-CrossSell",
  "#CartDrawer-Footer",
];
// Last server markup applied to each part, to skip swaps that would change nothing.
const cartDrawerRenderedHTML = new WeakMap();

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (!this.isOpen) return;
        setTabindex(this.toggleEelements(), "0");
        // Keep focus where the shopper is when they change the cart from inside it.
        if (!CART_ITEMS_SELF_REFRESH_SOURCES.has(event?.source)) {
          this.closeButton().focus();
        }
      },
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();

        // Save opener only when opening
        if (!this.isOpen) this.rememberOpener(e);

        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      if (section.id === "cart-drawer") {
        this.renderDrawer(parsedState.sections[section.id]);
        return;
      }
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  // Updates the drawer from a freshly rendered cart-drawer section. Only the parts that
  // depend on the cart are swapped (see the ids in snippets/cart-drawer.liquid), and only
  // when their markup changed, so Monk's tester picker, the list's scroll position and
  // the cross-sell's open state are kept. The whole drawer is rebuilt only when the
  // cart turns empty or stops being empty.
  renderDrawer(html) {
    const current = this.querySelector("#CartDrawer");
    const next = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector("#CartDrawer");
    if (!current || !next) return;

    const parts = CART_DRAWER_PARTS.map((selector) => [
      current.querySelector(selector),
      next.querySelector(selector),
    ]);
    if (parts.some(([from, to]) => !from !== !to)) {
      current.innerHTML = next.innerHTML;
      return;
    }

    const scroller = this.querySelector("cart-drawer-items");
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const crossSell = current.querySelector(".wt-cart__cross-sell");
    const crossSellCollapsed = crossSell
      ? crossSell.classList.contains("wt-cart__cross-sell--collapsed")
      : null;

    parts.forEach(([from, to]) => {
      if (!from || cartDrawerRenderedHTML.get(from) === to.innerHTML) return;
      from.innerHTML = to.innerHTML;
      cartDrawerRenderedHTML.set(from, to.innerHTML);
    });

    const newCrossSell = current.querySelector(".wt-cart__cross-sell");
    if (newCrossSell && newCrossSell !== crossSell && crossSellCollapsed !== null) {
      newCrossSell.classList.toggle("wt-cart__cross-sell--collapsed", crossSellCollapsed);
    }
    if (scroller) scroller.scrollTop = scrollTop;
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  onCartUpdate(event) {
    // product-form has already rendered the drawer from its own add response.
    if (event?.source === "product-form") return;
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        document.querySelector("cart-drawer")?.renderDrawer(responseText);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderSections(parsedState) {
    document
      .querySelector("cart-drawer")
      ?.renderDrawer(parsedState.sections["cart-drawer"]);
    const bubble = document.getElementById("cart-icon-bubble");
    const bubbleHTML = this.getSectionInnerHTML(
      parsedState.sections["cart-icon-bubble"],
      ".shopify-section",
    );
    if (bubble && bubbleHTML !== undefined) bubble.innerHTML = bubbleHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
